import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { ClientSessionConfiguration } from "abweb/dto/generated/session/ClientSessionConfiguration";
import { AbWebServices } from "abweb/service/AbWebServices";
import { MethodType } from "abweb/service/rpc/MethodType";
import { ProxyRPCService } from "abweb/service/rpc/ProxyRPCService";
import { ActivationManager } from "abweb/service/session/ActivationManager";
import { FetchUtils } from "abweb/service/utils/rpc/FetchUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { TestState } from "abweb/utils/test/TestState";
import { URLUtils } from "abweb/utils/URLUtils";
const LOG = LogUtils.getLogger(__filename);
export class ClientSessionService {
  constructor(rpcService, teardownService) {
    _defineProperty(this, "hasSession", false);
    _defineProperty(this, "_clientSessionInitParams", {});
    this.rpcService = rpcService;
    this.teardownService = teardownService;
  }
  get abHelpURL() {
    return this._abHelpURL;
  }
  get activationManager() {
    return this._activationManager;
  }
  get inactivityConfiguration() {
    return this._inactivityConfiguration;
  }
  get proxyConfiguration() {
    return this._proxyConfiguration;
  }
  get localServices() {
    return this._localServices;
  }
  get clientSessionInitParams() {
    return this._clientSessionInitParams;
  }
  set clientSessionInitParams(value) {
    this._clientSessionInitParams = value;
  }
  async reinitializeSession(resetProxies = true) {
    // Assumes singleton rpcService.clientSessionId is null at outset
    LOG.debug("reinitializeSession");
    if (resetProxies) {
      // Must call ProxyRPCService.reset() before below doSessionInit because as soon as clientSessionId
      // known (semaphore), resubscribes will immediately start happening (see AbStreamingHooks).
      ProxyRPCService.reset();
    }
    await this.doSessionInit(false, this.clientSessionInitParams);
  }
  initializeSession(namedParams) {
    return this.doSessionInit(true, namedParams);
  }

  /**
   * Special initialize function to be used ONLY FOR TESTING!
   *
   * Added as part of ABSDK-2300.
   * By default we avoid the websocket handshake (disableWS=true) since it's an issue in Firefox with Karma.
   */
  initializeSessionForTesting(namedParams, disableWS = true) {
    namedParams[ClientSessionService.TEST_ONLY_DISABLE_WS] = disableWS;
    return this.initializeSession(namedParams);
  }
  async initClientSession(namedParams, urlRoot = "") {
    // adds 'additional' props as query string
    namedParams[ClientSessionService.XX_FILE_BROWSER] = "true";
    const url = urlRoot + "/api/abweb/session/init";
    const retVal = await this.rpcService.invokeRetObj(url, namedParams, ClientSessionConfiguration, FetchUtils.buildFormRequest());
    if (retVal == null) {
      throw new Error("Null or undefined ClientSessionConfiguration returned from rpc.");
    }
    LOG.info(`Acquired clientSessionId='${retVal.id}'`);
    return retVal;
  }
  async releaseSession(doTeardown = true) {
    // Logout workflow: called from LogoutRouteHandler with doTeardown=false,
    //           then on app unload (doTeardown=true) but hasSession is now false so does nothing (app server logout tears down client session)
    // On navigate away from app, then only app unload (doTeardown=true) is invoked as want hail mary  to call invokeVoid below
    //           as app server logout will not be invoked (it tears down the client session on the server side)
    if (this.hasSession) {
      // Remember clientSessionId before below closeClientSession sets it to null
      const clientSessionId = this.rpcService.clientSessionId;
      if (!clientSessionId) {
        // defensive assertion
        LOG.error("Unable to release a session as clientSessionId is null.");
      }
      // then stop polling within rpc service
      this.rpcService.closeClientSession();
      if (doTeardown) {
        await this.invokeReleaseSession(clientSessionId);
      }
      this.hasSession = false;
    }
  }

  /**
   * Special release function to be used ONLY FOR TESTING!
   */
  async releaseSessionForTesting(doTeardown = true) {
    // The reason this testing-only method was created
    // This next line was added for ABSDK-2227 - we need to ignore errors after we've started shutting down
    LOG.debug("About to set shutdown flag on RPC service");
    AbWebServices.getRPCService().setShuttingDown();
    await this.releaseSession(doTeardown);
  }
  async doSessionInit(newSession, namedParams) {
    const hack_disableWS = namedParams ? namedParams[ClientSessionService.TEST_ONLY_DISABLE_WS] : false;
    this.clientSessionInitParams = namedParams;
    const clientIdOverride = namedParams ? namedParams[ClientSessionService.CLIENT_SESSION_ID] : null;
    const clientSessionParams = {};
    if (clientIdOverride) {
      clientSessionParams[ClientSessionService.CLIENT_SESSION_ID] = clientIdOverride;
      return this.rpcService.invokeRetObj("/api/abweb/session/configuration", clientSessionParams, ClientSessionConfiguration, MethodType.GET).then(configuration => {
        this._abHelpURL = configuration.abHelpURL;
        this._inactivityConfiguration = configuration.inactivityConfiguration;
        this._proxyConfiguration = configuration.proxyConfiguration;
        this._localServices = configuration.localServices;
        if (hack_disableWS) {
          LOG.debug("Override to disable websockets");
          configuration.pollConfiguration.isWSDisabled = true;
        }
        this.rpcService.setClientSessionId(clientIdOverride, configuration.pollConfiguration);
        this.hasSession = true;
        // TODO: EL - for now, don't even think about activation or unsaved changes
      });
    }
    const configuration = await this.initClientSession(namedParams);
    clientSessionParams[ClientSessionService.CLIENT_SESSION_ID] = configuration.id;
    this._abHelpURL = configuration.abHelpURL;
    this._inactivityConfiguration = configuration.inactivityConfiguration;
    this._proxyConfiguration = configuration.proxyConfiguration;
    this._localServices = configuration.localServices;
    if (hack_disableWS) {
      LOG.debug("Override to disable websockets");
      configuration.pollConfiguration.isWSDisabled = true;
    }
    this.rpcService.setClientSessionId(configuration.id, configuration.pollConfiguration);
    this.hasSession = true;
    if (!this._activationManager && configuration.activationConfiguration) {
      this._activationManager = new ActivationManager(this.rpcService, configuration.activationConfiguration);
    }
    if (newSession) {
      // Known issue: will not be invoked if 'Leave Site' dialog displays and they decide to leave the site (CAT-935)
      this.teardownService.teardownListenersRegistration.addCallback(() => {
        this.releaseSession();
      });
    }

    // TODO: consider allowing passing activationConfiguration through to an existing ActivationManager (like if
    // TODO: a Metadata Hub reconnects to the server).
  }
  async invokeReleaseSession(clientSessionId) {
    // abweb/session/release does NOT need the csrf header as it is on the CSRFPreventionFilter exclusion list
    // (plus sendBeacon does not support custom headers!).
    // Does need clientId, as rpcService closeSession already called which sets rpcService.clientSessionId to null
    let relUrl = `/api/abweb/session/release?${ClientSessionService.CLIENT_SESSION_ID}=${clientSessionId}`;
    if (navigator.sendBeacon == null) {
      relUrl = relUrl + "&sendBeaconNotSupported";
    } else if (TestState.TEST_MODE) {
      relUrl = relUrl + "&sendBeaconNotUsedInTestMode";
    } else {
      // CAT-1865: use sendBeacon when available to release session (full supported in Ab Initio stack)
      // However, sendBeacon is NOT supported in Web Workers, so defensively check
      // Do NOT use beacon during tests as prefer rpcService so that can be assured
      // it is released by awaiting returned promise (sendBeacon is fired on another thread, no guarantee when it finishes)
      const absUrl = URLUtils.getAbsoluteUrl(relUrl) + "&fromSendBeacon";
      LOG.debug(`About to release session with ${ClientSessionService.CLIENT_SESSION_ID}=${clientSessionId} via sendBeacon`);
      const beaconRequestQueued = navigator.sendBeacon(absUrl);
      if (beaconRequestQueued) {
        LOG.info(`sendBeacon releaseSession for clientSessionId='${clientSessionId}' successfully queued.`);
        return;
      }
      LOG.error("releaseSession sendBeacon failed with to queue beacon request, retrying via rpcService call");
      relUrl = relUrl + "&sendBeaconQueueFailed";
    }
    // We are in a Karma test, or a web worker that doesn't support sendBeacon, or sendBeacon above failed to queue.
    // Uses stronger await instead of a queued beacon so that we can be assured that release finishes.
    LOG.debug(`About to release session with clientSessionId=${clientSessionId} with rpcService call`);
    await this.rpcService.invokeVoid(relUrl, null, MethodType.POST);
    LOG.debug(`Finished releasing session with clientSessionId=${clientSessionId} with rpcService call`);
  }
}
_defineProperty(ClientSessionService, "CLIENT_SESSION_ID", "clientId");
_defineProperty(ClientSessionService, "XX_FILE_BROWSER", "xxfilebrowser");
_defineProperty(ClientSessionService, "TEST_ONLY_DISABLE_WS", "disableWS");
//# sourceMappingURL=ClientSessionService.js.map
